/**
 * @module engine/export/pdf/PdfCanvas
 * @summary A Canvas-2D-shaped façade that writes real PDF vectors instead of pixels.
 * @description
 *   The ~100 draw functions in `shapes/drawFns.ts` are the visual heart of the
 *   tool and they are ported VERBATIM from the original — nobody wants to
 *   rewrite them, and re-deriving 100 symbols in a second drawing language
 *   would guarantee the two drift apart. So instead of teaching the symbols
 *   about PDF, this class teaches PDF to answer to the Canvas 2D calls they
 *   already make.
 *
 *   Hand one of these to `drawFn(c, s)` and every `moveTo`/`arc`/`fillText`
 *   lands in the page's content stream as a path or a glyph. The result is a
 *   drawing made of LINES rather than a photograph of a drawing: it stays sharp
 *   at 1600% in Acrobat, prints correctly on a plotter, and the file is smaller
 *   than the JPEG it replaces.
 *
 *   ## What is implemented
 *   Exactly the subset the drawing code uses, and nothing else: paths (moveTo,
 *   lineTo, closePath, arc, ellipse, rect, roundRect, quadraticCurveTo,
 *   bezierCurveTo), painting (fill, stroke, fillRect, strokeRect, clip), state
 *   (save/restore, translate, rotate, scale, fillStyle, strokeStyle, lineWidth,
 *   globalAlpha, setLineDash, lineJoin, lineCap) and text (font, textAlign,
 *   textBaseline, fillText, measureText). Anything else is absent, so a symbol
 *   that reaches for it throws and the caller skips that one symbol.
 *
 *   ## Where it deliberately differs from a canvas
 *   - **Fonts.** Segoe UI is not in a PDF's base-14, so it is mapped to
 *     Helvetica. Widths differ by a few percent, which can nudge a centred
 *     label a hair off where the screen puts it. Substituting the metrics is the
 *     price of not embedding a 300 KB font in every drawing you email.
 *   - **Minimum line weight and text height.** Plotted at 1:200 a 1.5-unit line
 *     is 0.19 mm; at 1:1000 it is 0.04 mm, which is below what any plotter or
 *     screen can show and the drawing would come out blank-looking. Both are
 *     floored (see `minLineMm` / `minTextMm`) so reducing the scale can never
 *     make the drawing vanish.
 *   - **Path reuse.** Canvas keeps a path alive across `fill()` *and*
 *     `stroke()`; PDF's paint operators consume it. The path is therefore
 *     buffered here and re-emitted per paint, which is what makes the very
 *     common `c.fill();c.stroke();` come out right.
 *
 *   Coordinates go in as WORLD drawing units (what the draw functions use) and
 *   come out as millimetres on the page, via the matrix set up by `sheet.ts`.
 */
import type { jsPDF } from 'jspdf';
import { parseColor } from './color';

/**
 * A 2×3 affine matrix in Canvas order: `x' = a·x + c·y + e`, `y' = b·x + d·y + f`.
 * Same convention as `setTransform`, so the arithmetic below reads like the spec.
 */
interface Mat {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
}

/** Everything `save()` has to put back. */
interface GState {
  m: Mat;
  fillStyle: string;
  strokeStyle: string;
  lineWidth: number;
  globalAlpha: number;
  lineDash: number[];
  lineJoin: CanvasLineJoin;
  lineCap: CanvasLineCap;
  font: string;
  textAlign: CanvasTextAlign;
  textBaseline: CanvasTextBaseline;
}

/** One buffered path segment, already in page millimetres. */
type Seg =
  | { op: 'm'; c: [number, number] }
  | { op: 'l'; c: [number, number] }
  | { op: 'c'; c: [number, number, number, number, number, number] }
  | { op: 'h' };

export interface PdfCanvasOpts {
  /** Thinnest line that may reach the page, in mm. ISO's thinnest is 0.13. */
  minLineMm?: number;
  /** Smallest text height that may reach the page, in mm. */
  minTextMm?: number;
}

const identity = (): Mat => ({ a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 });

export class PdfCanvas {
  private readonly doc: jsPDF;
  private m: Mat = identity();
  private readonly stack: GState[] = [];
  /** The buffered current path, in page mm. */
  private segs: Seg[] = [];
  /** Sub-path start and current point, in WORLD units (for arc's implicit line). */
  private cur: { x: number; y: number } | null = null;
  private readonly minLineMm: number;
  private readonly minTextMm: number;
  /** Opacity → registered ExtGState key, so a drawing with 400 translucent
   *  shadows registers one graphics state rather than 400. */
  private readonly gsKeys = new Map<string, string>();
  private gsApplied = '';

  // ── Canvas state (public so the draw functions can read it back, which many
  //    of them do: `const og = c.fillStyle; … c.fillStyle = og;`) ──
  fillStyle = '#000000';
  strokeStyle = '#000000';
  lineWidth = 1;
  globalAlpha = 1;
  lineJoin: CanvasLineJoin = 'miter';
  lineCap: CanvasLineCap = 'butt';
  font = '10px sans-serif';
  textAlign: CanvasTextAlign = 'left';
  textBaseline: CanvasTextBaseline = 'alphabetic';
  private lineDash: number[] = [];

  constructor(doc: jsPDF, opts: PdfCanvasOpts = {}) {
    this.doc = doc;
    this.minLineMm = opts.minLineMm ?? 0.13;
    this.minTextMm = opts.minTextMm ?? 1.4;
  }

  // ═══════════════════════════ Transform ═══════════════════════════

  /** Replace the matrix outright — how `sheet.ts` installs the world→page map. */
  setTransform(a: number, b: number, c: number, d: number, e: number, f: number): void {
    this.m = { a, b, c, d, e, f };
  }

  translate(tx: number, ty: number): void {
    const { a, b, c, d, e, f } = this.m;
    this.m = { a, b, c, d, e: e + a * tx + c * ty, f: f + b * tx + d * ty };
  }

  scale(sx: number, sy: number): void {
    const { a, b, c, d, e, f } = this.m;
    this.m = { a: a * sx, b: b * sx, c: c * sy, d: d * sy, e, f };
  }

  /** Radians, clockwise on screen — the Canvas convention. */
  rotate(rad: number): void {
    const cs = Math.cos(rad);
    const sn = Math.sin(rad);
    const { a, b, c, d, e, f } = this.m;
    this.m = {
      a: a * cs + c * sn,
      b: b * cs + d * sn,
      c: a * -sn + c * cs,
      d: b * -sn + d * cs,
      e,
      f,
    };
  }

  /** World point → page millimetres. */
  private tp(x: number, y: number): [number, number] {
    const { a, b, c, d, e, f } = this.m;
    return [a * x + c * y + e, b * x + d * y + f];
  }

  /**
   * How much the matrix magnifies lengths, used for line widths, dashes and
   * font sizes. The export matrix is a uniform scale with an optional rotation,
   * so the geometric mean of the two column norms is exact for our case and a
   * sane average for any other.
   */
  private get scaleFactor(): number {
    const { a, b, c, d } = this.m;
    return Math.sqrt(Math.abs(a * d - b * c)) || Math.hypot(a, b) || 1;
  }

  /** Matrix rotation in degrees, clockwise — for placing rotated text. */
  private get rotationDeg(): number {
    return (Math.atan2(this.m.b, this.m.a) * 180) / Math.PI;
  }

  // ═══════════════════════════ State stack ═══════════════════════════

  save(): void {
    this.stack.push({
      m: { ...this.m },
      fillStyle: this.fillStyle,
      strokeStyle: this.strokeStyle,
      lineWidth: this.lineWidth,
      globalAlpha: this.globalAlpha,
      lineDash: this.lineDash.slice(),
      lineJoin: this.lineJoin,
      lineCap: this.lineCap,
      font: this.font,
      textAlign: this.textAlign,
      textBaseline: this.textBaseline,
    });
    // A PDF `q` as well, because clip() can only be undone by `Q`.
    this.doc.saveGraphicsState();
    // `Q` also rolls back colour and dash, and jsPDF's own idea of what it last
    // emitted does not roll back with it. Forget the cached ExtGState so the
    // next paint re-states everything rather than trusting a stale tracker.
    this.gsApplied = '';
  }

  restore(): void {
    const s = this.stack.pop();
    if (!s) return;
    this.doc.restoreGraphicsState();
    this.gsApplied = '';
    this.m = s.m;
    this.fillStyle = s.fillStyle;
    this.strokeStyle = s.strokeStyle;
    this.lineWidth = s.lineWidth;
    this.globalAlpha = s.globalAlpha;
    this.lineDash = s.lineDash;
    this.lineJoin = s.lineJoin;
    this.lineCap = s.lineCap;
    this.font = s.font;
    this.textAlign = s.textAlign;
    this.textBaseline = s.textBaseline;
  }

  /** How deep the state stack is — see `unwindTo`. */
  depth(): number {
    return this.stack.length;
  }

  /**
   * Pop back down to `n`. A symbol that throws between `save()` and `restore()`
   * would otherwise leave the PDF's `q`/`Q` nesting unbalanced and corrupt every
   * shape drawn after it, so the caller's catch block unwinds through here.
   */
  unwindTo(n: number): void {
    while (this.stack.length > n) this.restore();
  }

  // ═══════════════════════════ Path building ═══════════════════════════

  beginPath(): void {
    this.segs = [];
    this.cur = null;
  }

  moveTo(x: number, y: number): void {
    this.segs.push({ op: 'm', c: this.tp(x, y) });
    this.cur = { x, y };
  }

  lineTo(x: number, y: number): void {
    if (!this.cur) return this.moveTo(x, y);
    this.segs.push({ op: 'l', c: this.tp(x, y) });
    this.cur = { x, y };
  }

  closePath(): void {
    if (this.segs.length) this.segs.push({ op: 'h' });
  }

  bezierCurveTo(x1: number, y1: number, x2: number, y2: number, x: number, y: number): void {
    if (!this.cur) this.moveTo(x1, y1);
    const [a1, b1] = this.tp(x1, y1);
    const [a2, b2] = this.tp(x2, y2);
    const [a3, b3] = this.tp(x, y);
    this.segs.push({ op: 'c', c: [a1, b1, a2, b2, a3, b3] });
    this.cur = { x, y };
  }

  /** A quadratic promoted to the cubic PDF only knows how to draw. */
  quadraticCurveTo(cx: number, cy: number, x: number, y: number): void {
    const p0 = this.cur ?? { x: cx, y: cy };
    if (!this.cur) this.moveTo(cx, cy);
    this.bezierCurveTo(
      p0.x + (2 / 3) * (cx - p0.x),
      p0.y + (2 / 3) * (cy - p0.y),
      x + (2 / 3) * (cx - x),
      y + (2 / 3) * (cy - y),
      x,
      y,
    );
  }

  rect(x: number, y: number, w: number, h: number): void {
    this.moveTo(x, y);
    this.lineTo(x + w, y);
    this.lineTo(x + w, y + h);
    this.lineTo(x, y + h);
    this.closePath();
    // Canvas leaves the current point at the sub-path start after a rect.
    this.cur = { x, y };
  }

  roundRect(x: number, y: number, w: number, h: number, r: number | number[] = 0): void {
    const rr = Math.min(Array.isArray(r) ? Number(r[0]) || 0 : r, Math.abs(w) / 2, Math.abs(h) / 2);
    if (rr <= 0) return this.rect(x, y, w, h);
    const k = rr * (1 - 0.5522847498307936);
    this.moveTo(x + rr, y);
    this.lineTo(x + w - rr, y);
    this.bezierCurveTo(x + w - k, y, x + w, y + k, x + w, y + rr);
    this.lineTo(x + w, y + h - rr);
    this.bezierCurveTo(x + w, y + h - k, x + w - k, y + h, x + w - rr, y + h);
    this.lineTo(x + rr, y + h);
    this.bezierCurveTo(x + k, y + h, x, y + h - k, x, y + h - rr);
    this.lineTo(x, y + rr);
    this.bezierCurveTo(x, y + k, x + k, y, x + rr, y);
    this.closePath();
  }

  arc(cx: number, cy: number, r: number, a0: number, a1: number, ccw = false): void {
    this.ellipse(cx, cy, r, r, 0, a0, a1, ccw);
  }

  /**
   * Elliptical arc as a chain of cubic Béziers — the only curve a PDF has.
   * Each Bézier covers at most 90°, where the error of the standard
   * `4/3·tan(Δ/4)` control-point formula is under a thousandth of the radius:
   * far below anything a plotter or a screen can resolve.
   */
  ellipse(
    cx: number,
    cy: number,
    rx: number,
    ry: number,
    rot: number,
    a0: number,
    a1: number,
    ccw = false,
  ): void {
    // Canvas normalises the sweep by winding until it lies the right way round.
    const start = a0;
    let end = a1;
    if (!ccw) {
      while (end < start) end += Math.PI * 2;
      if (end - start > Math.PI * 2) end = start + Math.PI * 2;
    } else {
      while (end > start) end -= Math.PI * 2;
      if (start - end > Math.PI * 2) end = start - Math.PI * 2;
    }
    const sweep = end - start;
    const cosR = Math.cos(rot);
    const sinR = Math.sin(rot);
    // Point and tangent on the (possibly rotated) ellipse.
    const at = (t: number): [number, number] => {
      const ct = Math.cos(t);
      const st = Math.sin(t);
      return [cx + rx * ct * cosR - ry * st * sinR, cy + rx * ct * sinR + ry * st * cosR];
    };
    const dt = (t: number): [number, number] => {
      const ct = Math.cos(t);
      const st = Math.sin(t);
      return [-rx * st * cosR - ry * ct * sinR, -rx * st * sinR + ry * ct * cosR];
    };

    const [sx, sy] = at(start);
    // Canvas draws a straight line from the current point to the arc's start.
    if (this.cur) this.lineTo(sx, sy);
    else this.moveTo(sx, sy);

    const n = Math.max(1, Math.ceil(Math.abs(sweep) / (Math.PI / 2)));
    const step = sweep / n;
    const alpha = (4 / 3) * Math.tan(step / 4);
    let t = start;
    for (let i = 0; i < n; i++) {
      const t1 = t + step;
      const [p0x, p0y] = at(t);
      const [p3x, p3y] = at(t1);
      const [d0x, d0y] = dt(t);
      const [d1x, d1y] = dt(t1);
      this.bezierCurveTo(
        p0x + alpha * d0x,
        p0y + alpha * d0y,
        p3x - alpha * d1x,
        p3y - alpha * d1y,
        p3x,
        p3y,
      );
      t = t1;
    }
  }

  // ═══════════════════════════ Painting ═══════════════════════════

  fill(): void {
    if (!this.segs.length) return;
    this.applyFill();
    this.emit(this.segs);
    this.doc.fill();
  }

  stroke(): void {
    if (!this.segs.length) return;
    this.applyStroke();
    this.emit(this.segs);
    this.doc.stroke();
  }

  /** Intersect the clip region with the current path, consuming the path. */
  clip(): void {
    if (!this.segs.length) return;
    this.emit(this.segs);
    this.doc.clip().discardPath();
  }

  fillRect(x: number, y: number, w: number, h: number): void {
    if (!w || !h) return;
    this.applyFill();
    this.emit(this.rectSegs(x, y, w, h));
    this.doc.fill();
  }

  strokeRect(x: number, y: number, w: number, h: number): void {
    this.applyStroke();
    this.emit(this.rectSegs(x, y, w, h));
    this.doc.stroke();
  }

  /** Canvas's rect-painting calls leave the current path alone, so build separately. */
  private rectSegs(x: number, y: number, w: number, h: number): Seg[] {
    return [
      { op: 'm', c: this.tp(x, y) },
      { op: 'l', c: this.tp(x + w, y) },
      { op: 'l', c: this.tp(x + w, y + h) },
      { op: 'l', c: this.tp(x, y + h) },
      { op: 'h' },
    ];
  }

  private emit(segs: Seg[]): void {
    this.doc.path(segs as unknown[]);
  }

  // ═══════════════════════════ Text ═══════════════════════════

  fillText(txt: string, x: number, y: number): void {
    if (txt === null || txt === undefined || txt === '') return;
    const { sizeMm } = this.applyFont();
    if (sizeMm <= 0) return;
    const col = parseColor(this.fillStyle);
    this.applyAlpha(col.a * this.globalAlpha);
    this.doc.setTextColor(col.r, col.g, col.b);
    const [px, py] = this.tp(x, y);
    const rot = this.rotationDeg;
    this.doc.text(String(txt), px, py, {
      align: this.textAlign === 'center' ? 'center' : this.textAlign === 'right' ? 'right' : 'left',
      baseline: pdfBaseline(this.textBaseline),
      // rotationDirection 0 makes positive degrees turn clockwise, matching
      // Canvas's `rotate` — see the sign handling in jsPDF's text().
      ...(Math.abs(rot) > 0.01 ? { angle: rot, rotationDirection: 0 as const } : {}),
    });
  }

  /**
   * Text width in WORLD units, because that is what the callers measure in —
   * `drawFns` wraps note text and `routing` knocks a hole in a pipe behind a
   * label, both in drawing coordinates.
   */
  measureText(txt: string): TextMetrics {
    const { sizeWorld } = this.applyFont();
    const units = this.doc.getStringUnitWidth(String(txt ?? ''));
    return { width: units * sizeWorld } as TextMetrics;
  }

  /**
   * Push `this.font` into the document and report the size in both spaces.
   * Segoe UI has no PDF equivalent, so Helvetica stands in for it.
   */
  private applyFont(): { sizeWorld: number; sizeMm: number } {
    const f = parseFont(this.font);
    const k = this.scaleFactor;
    // Floor the PLOTTED height, not the world height, so a small plot scale
    // cannot shrink annotation below what a plotter can put on paper.
    const mm = Math.max(f.size * k, this.minTextMm);
    this.doc.setFont('helvetica', f.style);
    this.doc.setFontSize((mm * 72) / 25.4);
    return { sizeWorld: mm / k, sizeMm: mm };
  }

  // ═══════════════════════════ Line style ═══════════════════════════

  setLineDash(pattern: number[]): void {
    this.lineDash = Array.isArray(pattern) ? pattern.filter((v) => Number.isFinite(v)) : [];
  }

  getLineDash(): number[] {
    return this.lineDash.slice();
  }

  private applyFill(): void {
    const col = parseColor(this.fillStyle);
    this.applyAlpha(col.a * this.globalAlpha);
    this.doc.setFillColor(col.r, col.g, col.b);
  }

  private applyStroke(): void {
    const col = parseColor(this.strokeStyle);
    this.applyAlpha(col.a * this.globalAlpha);
    this.doc.setDrawColor(col.r, col.g, col.b);
    const k = this.scaleFactor;
    this.doc.setLineWidth(Math.max(this.lineWidth * k, this.minLineMm));
    this.doc.setLineJoin(this.lineJoin === 'miter' ? 'miter' : this.lineJoin);
    this.doc.setLineCap(this.lineCap === 'butt' ? 'butt' : this.lineCap);
    this.doc.setLineDashPattern(
      this.lineDash.map((v) => Math.max(v * k, 0.01)),
      0,
    );
  }

  /** Constant alpha lives in an ExtGState in PDF; register each value once. */
  private applyAlpha(a: number): void {
    const v = Math.max(0, Math.min(1, a));
    const key = 'op' + Math.round(v * 1000);
    if (this.gsApplied === key) return;
    if (!this.gsKeys.has(key)) {
      const Ctor = (this.doc as unknown as { GState: new (p: object) => object }).GState;
      this.doc.addGState(key, new Ctor({ opacity: v, 'stroke-opacity': v }) as never);
      this.gsKeys.set(key, key);
    }
    this.doc.setGState(key);
    this.gsApplied = key;
  }

  /**
   * The draw functions expect a `CanvasRenderingContext2D`. This is a faithful
   * stand-in for the subset they use and nothing more, so the cast is narrowed
   * to one place instead of littering every call site.
   */
  asContext(): CanvasRenderingContext2D {
    return this as unknown as CanvasRenderingContext2D;
  }
}

/** `'bold 10px Segoe UI'` → what jsPDF needs to know. */
function parseFont(font: string): { size: number; style: 'normal' | 'bold' | 'italic' | 'bolditalic' } {
  const bold = /(^|\s)(bold|[6-9]00)(\s|$)/.test(font);
  const italic = /(^|\s)(italic|oblique)(\s|$)/.test(font);
  const m = /(\d*\.?\d+)\s*px/.exec(font);
  return {
    size: m ? parseFloat(m[1]) : 10,
    style: bold && italic ? 'bolditalic' : bold ? 'bold' : italic ? 'italic' : 'normal',
  };
}

function pdfBaseline(b: CanvasTextBaseline): 'alphabetic' | 'top' | 'middle' | 'bottom' | 'hanging' {
  switch (b) {
    case 'top':
      return 'top';
    case 'middle':
      return 'middle';
    case 'bottom':
    case 'ideographic':
      return 'bottom';
    case 'hanging':
      return 'hanging';
    default:
      return 'alphabetic';
  }
}
